// @ts-nocheck
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'sag-ui-date-range-picker',
    template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    [id]="id"
                    type="text"
                    [disabled]="disabled"
                    [placeholder]="floatLabel ? '' : placeholder"
                    [readOnly]="readOnly"
                    [required]="invalid || (required && touched)"
                    [attr.pattern]="invalid ? 'SAG_INVALID_STATE' : null"
                    [attr.aria-label]="ariaLabel"
                    [attr.aria-labelledby]="ariaLabelledby || id"
                    [attr.aria-describedby]="ariaDescribedby"
                    [attr.data-float-label]="floatLabel"
                    [value]="displayValue"
                    (input)="blockChange($event)"
                    (click)="onClick($event)"
                    (focus)="onFocus($event)"
                    (blur)="onBlur($event)"
                    class="[appearance:textfield] [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
                <label
                    sag-ui-input-label
                    [htmlFor]="id"
                    [attr.data-float]="floatLabel ? focused || !!displayValue : undefined"
                    [attr.data-float-label]="floatLabel"
                >
                    <span *ngIf="required">*</span>
                    {{ label }}
                    <span
                        *ngIf="hint"
                        class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                        sag-ui-tooltip
                        [tooltip]="hint"
                    ></span>
                </label>
                <span class="absolute right-5 bottom-[0.8rem] z-10 icon-calendar text-black dark:text-white"></span>
                <div class="absolute top-full right-0 flex items-center z-20">
                    <sag-ui-calendar-range
                        *ngIf="showDatePicker"
                        [startDate]="startDate"
                        [endDate]="endDate"
                        [lang]="lang"
                        [availableRanges]="availableRanges"
                        (dateRangeChanged)="handleDateRangeChange($event)"
                    ></sag-ui-calendar-range>
                </div>
            </div>
            <div *ngIf="error && isArray(error)" class="flex flex-col gap-1">
                <span sag-ui-input-error *ngFor="let err of error">{{ err }}</span>
            </div>
            <span sag-ui-input-error *ngIf="error && !isArray(error)">{{ error }}</span>
        </div>
    `
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
    @Input() id = this.randomId();
    @Input() disabled = false;
    @Input() required = false;
    @Input() invalid: boolean | null = null;
    @Input() readOnly: boolean | null = null;
    @Input() error: string | string[] | null = null;
    @Input() placeholder = '';
    @Input() label = '';
    @Input() lang?: 'en' | 'de';
    @Input() availableRanges: { startDate: Date; endDate: Date }[] = [];
    @Input() hint?: string;
    @Input() floatLabel = false;

    @Input({ required: true }) formGroup!: FormGroup;
    @Input({ required: true }) formControlNameStart!: string;
    @Input({ required: true }) formControlNameEnd!: string;

    // Accessibility inputs
    @Input() ariaLabel: string | null = null;
    @Input() ariaLabelledby: string | null = null;
    @Input() ariaDescribedby: string | null = null;

    @Output() focus = new EventEmitter<Event>();
    @Output() blur = new EventEmitter<Event>();
    @Output() click = new EventEmitter<Event>();

    @HostListener('document:mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.showDatePicker = false;
        }
    }

    @HostListener('focusout', ['$event'])
    onFocusOut(event: FocusEvent): void {
        const focusedInside = this.el.nativeElement.contains(event.target);
        if (!focusedInside) {
            this.showDatePicker = false;
        }
    }

    private displayValueStart = '';
    private displayValueEnd = '';
    private destroy$ = new Subject<void>();

    protected showDatePicker = false;
    protected focused = false;

    constructor(private el: ElementRef) {}

    ngOnInit(): void {
        if (!this.formGroup || !this.formControlNameStart || !this.formControlNameEnd) {
            throw new Error(
                'Please provide a formGroup, formControlNameStart and formControlNameEnd to use the date range picker.'
            );
        }

        this.displayInitialValue();
        this.initFormControlsListeners();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initFormControlsListeners(): void {
        this.formGroup
            ?.get(this.formControlNameStart!)
            ?.valueChanges.pipe(
                takeUntil(this.destroy$),
                tap(value => {
                    this.displayValueStart = value ? (value as Date).toLocaleDateString() : '';
                })
            )
            .subscribe();

        this.formGroup
            ?.get(this.formControlNameEnd!)
            ?.valueChanges.pipe(
                takeUntil(this.destroy$),
                tap(value => {
                    this.displayValueEnd = value ? (value as Date).toLocaleDateString() : '';
                })
            )
            .subscribe();
    }

    private displayInitialValue(): void {
        if (this.formGroup && this.formControlNameStart) {
            this.displayValueStart = this.formGroup.get(this.formControlNameStart).value
                ? (this.formGroup.get(this.formControlNameStart).value as Date).toLocaleDateString()
                : '';
        }

        if (this.formGroup && this.formControlNameEnd) {
            this.displayValueEnd = this.formGroup.get(this.formControlNameEnd).value
                ? (this.formGroup.get(this.formControlNameEnd).value as Date).toLocaleDateString()
                : '';
        }
    }

    protected get displayValue(): string {
        if (this.displayValueStart && this.displayValueEnd) {
            return `${this.displayValueStart} - ${this.displayValueEnd}`;
        }

        return this.displayValueStart || this.displayValueEnd;
    }

    protected get touched(): boolean {
        return false;
    }

    onClick(event: Event): void {
        event.stopPropagation();
        this.click.emit(event);
        this.showDatePicker = true;
    }

    onFocus(event: Event): void {
        event.stopPropagation();
        this.focus.emit(event);
        this.showDatePicker = true;
        this.focused = true;
    }

    onBlur(event: Event): void {
        event.stopPropagation();
        this.focused = false;
        this.blur.emit(event);
    }

    blockChange(event: Event) {
        if (event.target && (event.target as any)['value']) {
            (event.target as any)['value'] = this.displayValue;
        }
    }

    isArray(value: string | string[]): value is string[] {
        return Array.isArray(value);
    }

    handleDateRangeChange(range: { startDate: Date | null; endDate: Date | null }): void {
        if (range.startDate && this.formGroup && this.formControlNameStart) {
            this.displayValueStart = range.startDate.toLocaleDateString();
            this.formGroup.get(this.formControlNameStart).setValue(range.startDate);
        } else {
            this.displayValueStart = '';
            this.formGroup.get(this.formControlNameStart).setValue(null);
        }

        if (range.endDate && this.formGroup && this.formControlNameEnd) {
            this.displayValueEnd = range.endDate.toLocaleDateString();
            this.formGroup.get(this.formControlNameEnd).setValue(range.endDate);
        } else {
            this.displayValueEnd = '';
            this.formGroup.get(this.formControlNameEnd).setValue(null);
        }
    }

    protected get startDate(): Date | undefined {
        return this.formGroup.get(this.formControlNameStart).value;
    }

    protected get endDate(): Date | undefined {
        return this.formGroup.get(this.formControlNameEnd).value;
    }

    private randomId(): string {
        return Math.random().toString(36).substring(2);
    }
}
